import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Permission codes matching the BE's ENUM_PERMISSION_CODE values.
 * These correspond to the `code` field in the permissions collection.
 */
export const PERMISSION_CODES = {
  DASHBOARD: "dashboard",
  WARDS: "wards",
  USERS: "users",
  PUBLIC_SERVICE_LINKS: "publicservicelinks",
  FEEDBACKS: "feedbacks",
  NEWS: "news",
  APPOINTMENTS: "appointments",
  AREA_OFFICERS: "areaofficers",
  PERMISSIONS: "permissions",
  AREAS: "areas",
  CHATS: "chats",
  AI_TRAINING: "ai-training",
  COMMITMENTS: "commitments",
  MINIAPP_ACCOUNTS: "miniappaccounts",
  FEEDBACK_BY_DOMAIN: "feedback-by-domain",
  MINIAPP_USERS: "miniapp-users",
  NEWS_ANALYTICS: "news-analytics",
  TASK_MANAGERMENTS: "task-managements",
  BROADCASTS: "broadcasts",
  ESTABLISHING_HOUSE_NUMBERS: "establishing-house-numbers",
  FEEDBACK_BY_OFFICER: "officer-feedbacks",
  AUDIT_LOGS: "audit-logs",
  AI_USAGE_STATS: "ai-usage-stats",
};

const MENU_CODE_BY_PERMISSION_CODE = {
  dashboard: "Dashboard",
  wards: "Setting.Ward",
  users: "UserRole",
  publicservicelinks: "PublicServiceLinks",
  feedbacks: "Feedback",
  news: "News",
  appointments: "Appointment",
  areaofficers: "AreaOfficer",
  areas: "Area",
  chats: "AIConversation",
  "ai-training": "AITraining",
  commitments: "Commitment",
  miniappaccounts: "MiniAppAccount",
  "feedback-by-domain": "FeedbackByDomain",
  "miniapp-users": "MiniAppUsers",
  "news-analytics": "NewsAnalytics",
  "task-managements": "TaskManagement",
  broadcasts: "Broadcast",
  "establishing-house-numbers": "HouseNumber",
  "officer-feedbacks": "OfficerFeedback",
  "audit-logs": "ActivityLog",
  "ai-usage-stats": "AIUsageStats",
};

const normalizePermissionKey = (value) =>
  String(value || "").replace(/[-_.]/g, "").toLowerCase();

/**
 * Maps FE route paths to the required permission code(s).
 * Routes not listed here are accessible to all authenticated users.
 */
export const ROUTE_PERMISSION_MAP = {
  "/": PERMISSION_CODES.DASHBOARD,
  "/news": PERMISSION_CODES.NEWS,
  "/feedback": PERMISSION_CODES.FEEDBACKS,
  "/appointments": PERMISSION_CODES.APPOINTMENTS,
  "/services": PERMISSION_CODES.PUBLIC_SERVICE_LINKS,
  "/ai-conversations": PERMISSION_CODES.CHATS,
  "/ai-training": PERMISSION_CODES.AI_TRAINING,
  "/officers": PERMISSION_CODES.AREA_OFFICERS,
  "/areas": PERMISSION_CODES.AREAS,
  "/users": PERMISSION_CODES.USERS,
  "/settings": PERMISSION_CODES.WARDS,
  "/commitment": PERMISSION_CODES.COMMITMENTS,
  "/miniapp-accounts": PERMISSION_CODES.MINIAPP_ACCOUNTS,
  "/feedback-domain": PERMISSION_CODES.FEEDBACK_BY_DOMAIN,
  "/miniapp-users-dashboard": PERMISSION_CODES.MINIAPP_USERS,
  "/news-analytics": PERMISSION_CODES.NEWS_ANALYTICS,
  "/task-managements": PERMISSION_CODES.TASK_MANAGERMENTS,
  "/broadcasts": PERMISSION_CODES.BROADCASTS,
  "/establishing-house-numbers": PERMISSION_CODES.ESTABLISHING_HOUSE_NUMBERS,
  "/audit-logs": PERMISSION_CODES.AUDIT_LOGS,
  "/officer-feedbacks": PERMISSION_CODES.FEEDBACK_BY_OFFICER,
  "/ai-usage-stats": PERMISSION_CODES.AI_USAGE_STATS,
};

/**
 * Hook: usePermission
 *
 * Extracts the current user's permission codes from Redux state.
 * The user object from BE has shape:
 *   user.roleId: role hệ thống theo chức vụ
 *   user.additionalRoleIds: các role bổ sung được gán riêng cho user
 *
 * Returns:
 *   - permissionCodes: Set<string> of permission codes
 *   - hasPermission(code): check if user has a specific permission
 *   - hasRouteAccess(path): check if user can access a route
 *   - filterMenuGroups(groups): filter sidebar menu groups by permission
 */
export const usePermission = () => {
  const user = useSelector((state) => state.auth.user);

  const actionPermissionCodes = useMemo(() => {
    const codes = new Set();
    const roles = [user?.roleId, ...(user?.additionalRoleIds || [])].filter(
      (role) => role && role.isActive !== false,
    );

    roles.forEach((role) => {
      (role?.permissionCodes || []).forEach((code) => codes.add(code));
      (role?.permissions || []).forEach((permission) => {
        (permission?.actions || []).forEach((action) => {
          codes.add(`${permission.menuCode}.${action}`);
        });
      });
    });

    return codes;
  }, [user]);

  const permissionCodes = useMemo(() => {
    const codes = new Set();
    const roles = [user?.roleId, ...(user?.additionalRoleIds || [])].filter(
      (role) => role && role.isActive !== false,
    );

    roles.forEach((role) => {
      if (Array.isArray(role?.permissionResources)) {
        role.permissionResources.forEach((permission) => {
          if (permission?.code) codes.add(permission.code);
        });
      }

      if (Array.isArray(role?.permissions)) {
        role.permissions.forEach((permission) => {
          const menuCode = String(permission?.menuCode || "").toLowerCase();
          const matchedCode = Object.values(PERMISSION_CODES).find(
            (code) => code.replace(/[-_.]/g, "") === menuCode.replace(/[-_.]/g, ""),
          );
          if (matchedCode) codes.add(matchedCode);
        });
      }
    });
    return codes;
  }, [user]);

  /**
   * Check if the user has a specific permission code.
   */
  const hasPermission = (code) => {
    if (user?.isAdmin) return true;
    if (
      code === PERMISSION_CODES.CHATS &&
      user?.position === "TRUONG_CONG_AN_PHUONG"
    ) {
      return true;
    }
    if (
      code === PERMISSION_CODES.AUDIT_LOGS &&
      (user?.position === "TRUONG_CONG_AN_PHUONG" ||
        user?.position === "PHO_TRUONG_CONG_AN_PHUONG")
    ) {
      return true;
    }
    if (!code) return true;
    return permissionCodes.has(code);
  };

  const hasActionPermission = (menuCode, actionCode) => {
    if (user?.isAdmin) return true;
    if (!menuCode || !actionCode) return false;
    if (actionPermissionCodes.has(`${menuCode}.${actionCode}`)) return true;

    const normalizedMenuCode = normalizePermissionKey(menuCode);
    const roles = [user?.roleId, ...(user?.additionalRoleIds || [])].filter(
      (role) => role && role.isActive !== false,
    );

    return roles.some((role) => {
      const hasDetailedPermission = (role?.permissions || []).some(
        (permission) =>
          normalizePermissionKey(permission?.menuCode) === normalizedMenuCode,
      );
      if (hasDetailedPermission) return false;

      return (role?.permissionResources || []).some(
        (permission) =>
          normalizePermissionKey(
            MENU_CODE_BY_PERMISSION_CODE[permission?.code],
          ) === normalizedMenuCode,
      );
    });
  };

  /**
   * Check if the user can access a given route path.
   * Routes without a mapping in ROUTE_PERMISSION_MAP are always accessible.
   */
  const hasRouteAccess = (path) => {
    // Find the matching permission by checking exact or prefix match
    const requiredCode = ROUTE_PERMISSION_MAP[path];
    if (!requiredCode) {
      // Check prefix match for nested routes (e.g., /commitment/new → /commitment)
      const matchedKey = Object.keys(ROUTE_PERMISSION_MAP).find(
        (key) => key !== "/" && path.startsWith(key + "/"),
      );
      if (matchedKey) {
        return hasPermission(ROUTE_PERMISSION_MAP[matchedKey]);
      }
      return true; // No permission required for this route
    }
    return hasPermission(requiredCode);
  };

  /**
   * Filter MENU_GROUPS to only include items the user has access to.
   * Uses item.permission if present, otherwise falls back to route-based check.
   * Removes entire groups if all items are filtered out.
   */
  const filterMenuGroups = (groups) => {
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.adminOnly && !user?.isAdmin) {
            return false;
          }

          return item.permission
            ? hasPermission(item.permission)
            : hasRouteAccess(item.key);
        }),
      }))
      .filter((group) => group.items.length > 0);
  };

  return {
    permissionCodes,
    actionPermissionCodes,
    hasPermission,
    hasActionPermission,
    hasRouteAccess,
    filterMenuGroups,
  };
};

export const useModuleActionPermissions = (menuCode) => {
  const { hasActionPermission } = usePermission();

  return {
    canCreate: hasActionPermission(menuCode, "Create"),
    canUpdate: hasActionPermission(menuCode, "Update"),
    canDelete: hasActionPermission(menuCode, "Delete"),
    canConfigure: hasActionPermission(menuCode, "Configure"),
  };
};
